import React from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../../hooks/useTheme';

interface BarChartProps {
  data: any[];
  title: string;
  loading: boolean;
}

const BarChart: React.FC<BarChartProps> = ({ data, title, loading }) => {
  const theme = useTheme();
  
  if (loading || !data || data.length === 0) {
    return <div>Sin datos</div>;
  }

  // Transformar y validar datos
  const transformedData = data
    .map(item => ({
      name: item.name || item.product_name || item.category || 'Sin nombre',
      value: item.value || item.quantity_sold || item.total_line_amount || 0
    }))
    .filter(item => {
      // Filtrar valores NaN, null, undefined o negativos
      const isValid = !isNaN(item.value) && 
                     item.value !== null && 
                     item.value !== undefined && 
                     item.value >= 0;
      if (!isValid) {
        console.warn(`⚠️ Valor inválido filtrado en BarChart:`, item);
      }
      return isValid;
    });

  if (transformedData.length === 0) {
    return <div>Sin datos válidos</div>;
  }

  // Asegurar que todos los valores sean números enteros para evitar problemas de precisión
  const sanitizedData = transformedData.map(item => ({
    ...item,
    value: Math.round(item.value * 100) / 100 // Redondear a 2 decimales
  }));

  // Calcular el rango de valores para configurar el dominio del eje Y
  const values = sanitizedData.map(item => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  // Asegurar que el dominio sea válido
  const domain = [
    Math.max(0, Math.floor(minValue * 0.9)), 
    Math.ceil(maxValue * 1.1)
  ];

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <ResponsiveContainer>
        <RechartsBarChart data={sanitizedData}>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={theme.colors.text.muted}
            strokeOpacity={0.3}
          />
          <XAxis 
            dataKey="name" 
            tick={{ fill: theme.colors.text.secondary, fontSize: 12 }}
            axisLine={{ stroke: theme.colors.text.muted }}
            tickLine={{ stroke: theme.colors.text.muted }}
            allowDataOverflow={false}
            scale="band"
          />
          <YAxis 
            domain={domain}
            tick={{ fill: theme.colors.text.secondary, fontSize: 12 }}
            axisLine={{ stroke: theme.colors.text.muted }}
            tickLine={{ stroke: theme.colors.text.muted }}
            allowDataOverflow={false}
            scale="linear"
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: theme.colors.background.card,
              border: `1px solid ${theme.colors.text.muted}`,
              borderRadius: theme.radius.md,
              boxShadow: theme.shadows.md,
            }}
            labelStyle={{ color: theme.colors.text.primary }}
          />
                     <Bar 
             dataKey="value" 
             fill={theme.colors.chart.primary}
             radius={[Number(theme.radius.sm), Number(theme.radius.sm), 0, 0]}
             isAnimationActive={false}
           />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart; 